import type { MouseEvent } from 'react';

import { getAndroidMajorVersion, isAndroidDevice } from './deviceDetection';

interface AndroidPdfBridge {
  getBase64FromBlobData(base64DataUrl: string): void;
}

/**
 * Android 10 (API 29) is where the shell's native handler writes through
 * MediaStore, which needs no runtime permission. Below that it falls back to
 * `getExternalStoragePublicDirectory`, which needs WRITE_EXTERNAL_STORAGE —
 * and the shell only requests that from its own download listener
 * (BaseWebViewDialog#downloadBlobPdf), never when the page calls the bridge
 * directly. So on 8.1/9 the write would throw inside the WebView's Java layer,
 * where the framework swallows the exception and we would have cancelled the
 * navigation for nothing. We stay out of the way there and let the anchor
 * navigate, which is what feeds that listener in the first place.
 */
const MIN_ANDROID_MAJOR_VERSION = 10;

/**
 * The Android shell injects this bridge into its in-app WebView as
 * `BlobDownload` (BaseWebViewDialog#configureWebView). It writes the PDF to
 * the device's Downloads collection and hands it to an external viewer via
 * ACTION_VIEW.
 *
 * This is Android-only by design: iOS WKWebView renders PDFs itself and every
 * desktop/mobile browser handles blob URLs fine, so those keep the standard
 * `window.open` / anchor navigation. We check the user agent first so the
 * workaround can only ever engage on Android, then probe for the bridge — we
 * deliberately do not use the `isMobileAppUser` session flag as the
 * in-app-WebView signal, because that flag is only set when the shell launches
 * us through the email/custKey flow, not the MWG/ACA token flow, so it is
 * absent for a large share of real webview traffic. The bridge probe is exact:
 * it exists only where we can actually use it.
 */
const getBridge = (): AndroidPdfBridge | undefined => {
  if (!isAndroidDevice()) return undefined;

  const androidMajorVersion = getAndroidMajorVersion();

  if (
    androidMajorVersion === undefined ||
    androidMajorVersion < MIN_ANDROID_MAJOR_VERSION
  ) {
    return undefined;
  }

  const bridge = (window as unknown as { BlobDownload?: AndroidPdfBridge })
    .BlobDownload;

  return typeof bridge?.getBase64FromBlobData === 'function'
    ? bridge
    : undefined;
};

/**
 * True when we are inside the Android in-app WebView and can hand PDFs to the
 * native viewer. Callers use this to decide whether to suppress an anchor's
 * default navigation.
 */
export const canOpenPdfNatively = (): boolean => getBridge() !== undefined;

const blobUriToDataUrl = async (blobUri: string): Promise<string> => {
  const blob = await (await fetch(blobUri)).blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
};

/**
 * Opens a PDF that we hold as a blob URL.
 *
 * In a browser this is just `window.open`. Inside the Android in-app WebView
 * that does not work: Android WebView ships without a PDF renderer, and the
 * shell's popup handling rewrites the URL of any window we open — appending
 * Adobe tracking params, which destroys the blob URL's identity and yields
 * ERR_FILE_NOT_FOUND. So we resolve the blob to bytes here, in the document
 * that created it (the only context where a blob URL is resolvable), and pass
 * those bytes to the native bridge instead of navigating anywhere.
 *
 */
export const openPdfDocument = async (blobUri: string): Promise<void> => {
  const bridge = getBridge();

  if (!bridge) {
    window.open(blobUri, '_blank', 'noopener,noreferrer');
    return;
  }

  bridge.getBase64FromBlobData(await blobUriToDataUrl(blobUri));
};

/**
 * Click handler for an anchor whose href is a PDF blob URL. Leaves the anchor
 * alone in a browser so that middle-click, right-click-save and desktop
 * behaviour are untouched; only diverts inside the Android WebView, where the
 * default navigation is broken.
 */
export const handlePdfAnchorClick = (
  event: MouseEvent<HTMLElement>,
  blobUri?: string
): void => {
  // Some callers share a handler across links that are not all blob PDFs
  // (the Welcome cards route both route paths and document URLs through one
  // href), so only divert what we can actually hand to the bridge.
  if (!blobUri?.startsWith('blob:') || !canOpenPdfNatively()) {
    return;
  }

  event.preventDefault();
  void openPdfDocument(blobUri);
};
